import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import Header from '../../components/Header/Header';
import { createStripeCheckoutSession } from '../../lib/stripeFunctions';
import { normalizeHotelRouteId, buildPaymentPath } from '../../lib/hotelRoutes';
import { useCurrency } from '../../context/CurrencyContext';
import './Payment.css';

const PAY_STORAGE_PREFIX = 'tourasco_pay_';

const Payment = () => {
  const { selectedCurrency, formatFrom, convert } = useCurrency();
  const { id: rawRouteId } = useParams();
  const id = useMemo(() => normalizeHotelRouteId(rawRouteId), [rawRouteId]);
  const location = useLocation();
  const navigate = useNavigate();

  const {
    hotel,
    searchParams,
    selectedOption,
    priceResults,
    guestDetails,
    bookingParams: prebuiltBookingParams,
  } = location.state || {};

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const steps = ['Your Selection', 'Your Details', 'Finish'];

  const checkInDate = searchParams?.checkInDate || '';
  const nights = searchParams?.nights || 1;
  const adults = searchParams?.adults || 1;
  const children = searchParams?.children || 0;
  const totalPrice = selectedOption?.totalPrice || 0;
  const insurancePrice = 0;
  const finalTotal = totalPrice + insurancePrice;

  const calculateCheckOut = (checkIn, n) => {
    if (!checkIn) return '';
    const d = new Date(checkIn);
    d.setDate(d.getDate() + n);
    return d.toISOString().split('T')[0];
  };

  const checkOutDate = calculateCheckOut(checkInDate, nights);

  const buildBookingParamsIfNeeded = useCallback(() => {
    if (prebuiltBookingParams) return prebuiltBookingParams;

    const contextId =
      priceResults?.contextId ||
      priceResults?.context?.contextId ||
      priceResults?.propertyOption?.[0]?.contextId ||
      selectedOption?.contextId;

    const bookingKey =
      priceResults?.bookingKey ||
      priceResults?.propertyOption?.[0]?.bookingKey ||
      selectedOption?.bookingKey;

    const propertyId =
      priceResults?.propertyOption?.[0]?.propertyId ||
      selectedOption?.propertyId ||
      hotel?.id ||
      hotel?.propertyId ||
      id;

    if (!contextId || !bookingKey || !propertyId) {
      return null;
    }

    const passengers = [];
    const childAges = searchParams?.childAges || [];

    passengers.push({
      id: Number(1),
      title: guestDetails.title || 'Mr',
      firstName: guestDetails.firstName,
      lastName: guestDetails.lastName,
      phone: guestDetails.phoneCode + guestDetails.phone,
      lead: true,
    });

    for (let i = 2; i <= adults; i++) {
      passengers.push({
        id: Number(i),
        title: 'Mr',
        firstName: guestDetails.firstName,
        lastName: guestDetails.lastName,
        phone: guestDetails.phoneCode + guestDetails.phone,
        lead: false,
      });
    }

    for (let i = 0; i < children; i++) {
      const childPassenger = {
        id: Number(adults + i + 1),
        title: 'Mr',
        firstName: guestDetails.firstName,
        lastName: guestDetails.lastName,
        phone: guestDetails.phoneCode + guestDetails.phone,
        lead: false,
      };
      if (childAges[i] && childAges[i] > 0) {
        childPassenger.age = Number(childAges[i]);
      }
      passengers.push(childPassenger);
    }

    const roomPrice = selectedOption?.roomPrice?.[0];
    const ratePlanCode = roomPrice?.ratePlan?.code || roomPrice?.ratePlanCode || '';
    const roomCode = roomPrice?.roomCode || '';

    if (!ratePlanCode || !roomCode) {
      return null;
    }

    return {
      contextId,
      bookingKey,
      propertyId,
      distributionId: searchParams?.distributionId || 'PL1662',
      nationality: searchParams?.nationality || guestDetails.country || 'GB',
      checkInDate,
      nights,
      currency: searchParams?.currency || 'GBP',
      roomPax: [
        {
          id: Number(1),
          adults: Number(adults),
          children: Number(children),
          ratePlanCode: String(ratePlanCode),
          roomCode: String(roomCode),
          ...(childAges.length > 0 && childAges.every((age) => age > 0)
            ? { childAges: childAges.map((age) => Number(age)) }
            : {}),
          passenger: passengers,
        },
      ],
      clientRef: `WEB-${guestDetails.firstName}${guestDetails.lastName}${Date.now()}`,
      timeoutMillis: 120000,
      includeRoomNames: true,
    };
  }, [
    prebuiltBookingParams,
    priceResults,
    selectedOption,
    hotel,
    id,
    searchParams,
    guestDetails,
    checkInDate,
    nights,
    adults,
    children,
  ]);

  const handlePayWithStripe = async () => {
    setError('');

    const bookingParams = buildBookingParamsIfNeeded();
    if (!bookingParams) {
      setError('Missing booking information. Please go back and try again.');
      return;
    }

    setLoading(true);

    try {
      const resumeKey =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

      const payload = {
        bookingParams,
        hotel,
        searchParams,
        guestDetails,
        totalPrice: finalTotal,
        checkInDate,
        checkOutDate,
        nights,
        adults,
        children,
      };

      sessionStorage.setItem(PAY_STORAGE_PREFIX + resumeKey, JSON.stringify(payload));

      const checkoutUrl = await createStripeCheckoutSession({
        amount: convert(finalTotal, 'GBP'),
        currency: selectedCurrency.toLowerCase(),
        hotelName: hotel?.name || 'Hotel booking',
        propertyId: id,
        resumeKey,
        cancelPath: buildPaymentPath(id),
      });

      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Stripe checkout error:', err);
      setError(err.message || 'Could not start payment. Try again.');
      setLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      home: '/',
      listing: '/hotels',
      about: '/about',
      contact: '/contact',
      'test-g2': '/test-g2',
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  if (!hotel || !selectedOption || !guestDetails) {
    return (
      <>
        <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
        <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh', pb: 6 }}>
          <Container sx={{ mt: 4 }}>
            <Alert severity="error">Payment information missing. Please go back and try again.</Alert>
            <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
              Go Back
            </Button>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
      <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', pb: 6 }}>
        <Container maxWidth="lg" sx={{ pt: 4 }}>
          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Stepper activeStep={2} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', lg: 'row' } }}>
            <Box sx={{ flex: { xs: 1, lg: '0 0 65%' } }}>
              <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  Pay with Stripe
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  You will be redirected to Stripe’s secure checkout to pay by card. We do not store
                  your card details on this site.
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    border: '2px solid #635BFF',
                    borderRadius: 2,
                    backgroundColor: '#fafaff',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#635BFF', mb: 1 }}>
                    Stripe
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Debit and credit cards (Visa, Mastercard, Amex, and more) via Stripe Checkout.
                  </Typography>
                </Paper>

                {error ? (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                ) : null}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handlePayWithStripe}
                  disabled={loading}
                  sx={{
                    py: 2,
                    fontSize: '18px',
                    fontWeight: 600,
                    backgroundColor: '#635BFF',
                    '&:hover': { backgroundColor: '#5851e6' },
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={24} sx={{ mr: 2, color: 'white' }} />
                      Redirecting to Stripe…
                    </>
                  ) : (
                    'Continue to Stripe Checkout'
                  )}
                </Button>
              </Paper>
            </Box>

            <Box sx={{ flex: { xs: 1, lg: '0 0 35%' } }}>
              <Paper elevation={2} sx={{ p: 3, position: 'sticky', top: 100 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Booking Summary
                </Typography>
                <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #eee' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {hotel?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {hotel?.address1}
                    {hotel?.cityName && `, ${hotel?.cityName}`}
                    {hotel?.countryName && `, ${hotel?.countryName}`}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Check-in
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(checkInDate).toLocaleDateString('en-GB', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Check-out
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(checkOutDate).toLocaleDateString('en-GB', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Duration
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {nights} {nights === 1 ? 'night' : 'nights'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Guests
                  </Typography>
                  <Typography variant="body1">
                    {adults} {adults === 1 ? 'adult' : 'adults'}
                    {children > 0 && `, ${children} ${children === 1 ? 'child' : 'children'}`}
                  </Typography>
                </Box>
                <Box sx={{ pt: 3, borderTop: '2px solid #eee' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Room Price</Typography>
                    <Typography variant="body2">{formatFrom(totalPrice, 'GBP')}</Typography>
                  </Box>
                  {insurancePrice > 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2">Insurance</Typography>
                      <Typography variant="body2">{formatFrom(insurancePrice, 'GBP')}</Typography>
                    </Box>
                  ) : null}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid #ddd',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#112A41' }}>
                      {formatFrom(finalTotal, 'GBP')}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Payment;
